import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function DiagnosticCTA() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    sector: '',
    website: '',
    email: '',
    challenge: ''
  });
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const response = await fetch('https://formspree.io/f/xjgdkoor', {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: new FormData(e.currentTarget)
      });
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setError('Hubo un problema al enviar tu solicitud. Intenta de nuevo en unos minutos.');
      }
    } catch {
      setError('No pudimos enviar tu solicitud. Verifica tu conexión e intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const inputClass = 'bg-transparent border border-arena/20 rounded-[6px] px-4 py-[14px] font-body text-[15px] text-arena placeholder:text-arena/35 focus:outline-none focus:border-arena/60 transition-colors duration-200 mb-4';
  return <section className="bg-verde text-arena py-[72px] md:py-[120px]" data-id="element-149" data-mp="3auiyhasveb0r">
      <div className="max-w-[1200px] mx-auto px-6 md:px-20" data-id="element-150" data-mp="3aroetlhoku8e">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="mb-12 md:mb-[64px]" data-id="element-151" data-mp="36tqmi9sm5s0s">
          <span className="block font-body text-[11px] uppercase font-medium text-arena/50 tracking-widest mb-4" data-id="element-152" data-mp="3girasgvzrwl7">
            DIAGNÓSTICO DE TU SITIO WEB
          </span>
          <h2 className="font-display text-[32px] md:text-[48px] font-bold text-arena leading-[1.1] max-w-[560px] mb-6" data-id="element-153" data-mp="3qgox2yuajjb6">
            Descubre qué está fallando en tu sitio web.
          </h2>
          <p className="font-body text-[16px] md:text-[20px] text-arena/70 leading-[1.6] max-w-[480px]" data-id="element-154" data-mp="3axq1xek561r">
            En 48 horas recibirás el análisis completo de tu sitio web en las
            cinco dimensiones del ICD™, con las brechas priorizadas y
            recomendaciones específicas.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24" data-id="element-155" data-mp="i26n2rkshtox">
          {/* Left Column */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          ease: 'easeOut',
          delay: 0.1
        }} data-id="element-156" data-mp="m2qm60oxb7dp">
            <h3 className="font-display text-[20px] font-semibold text-arena mb-6" data-id="element-157" data-mp="1smtabqwy066v">
              El diagnóstico incluye:
            </h3>

            <div className="space-y-4 mb-8" data-id="element-158" data-mp="2l8vvb1x1knos">
              {['ICD™ de tu sitio web (puntuación 0-100)', 'Qué está fallando en cada una de las 5 dimensiones', 'Las 3 correcciones de mayor impacto para tu sitio', 'Recomendaciones específicas para tu sitio web', 'Cómo se compara tu sitio con el promedio del sector'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-159" data-mp="346p069r877ot" data-mpi={idx}>
                  <span className="text-salvia mt-1" data-id="element-160" data-mp="2ooxdlxqk58vz">→</span>
                  <p className="font-body text-[16px] text-arena/80 leading-[1.6]" data-id="element-161" data-mp="2srlpkeakivx6">
                    {item}
                  </p>
                </div>)}
            </div>

            <div className="h-px w-full bg-arena/15 mb-6" data-id="element-162" data-mp="2eu5id0kg6y4d"></div>

            <div className="flex flex-col gap-1 mb-8" data-id="element-163" data-mp="2jrfet0698a1p">
              <span className="font-body text-[14px] text-arena/50 line-through" data-id="element-164" data-mp="37gq9bzx0h87e">
                Valor: $500.000 COP
              </span>
              <span className="font-body text-[16px] font-semibold text-arena" data-id="element-165" data-mp="3ce9n6ypsriyt">
                Sin costo en esta etapa
              </span>
            </div>

            <div className="h-px w-full bg-arena/15 mb-8" data-id="element-166" data-mp="32phyr0jy3gsn"></div>

            <h4 className="font-display text-[16px] font-semibold text-arena mb-3" data-id="element-167" data-mp="32nopddhy6igy">
              ¿Por qué es gratuito?
            </h4>
            <p className="font-body text-[15px] text-arena/65 leading-[1.6] mb-4" data-id="element-168" data-mp="3v6kio7i4gemw">
              Porque creemos que cuando veas exactamente qué está fallando en tu
              sitio web, querrás corregirlo. El diagnóstico es nuestra forma de
              demostrarte cómo lo hacemos.
            </p>

            <div className="h-px w-full bg-arena/15 my-8" data-id="element-169" data-mp="1sjx0rxf0qcw9"></div>

            <h4 className="font-display text-[14px] font-semibold text-arena mb-3" data-id="element-170" data-mp="1sl85q0k79qoy">
              Cómo es diferente.
            </h4>
            <p className="font-body text-[14px] text-arena/60 leading-[1.6]" data-id="element-171" data-mp="10rplb0b916jg">
              Cada diagnóstico lo realiza el equipo de Origen Studio
              manualmente. No hay plantillas, no hay automatizaciones, no hay
              respuestas genéricas. Analizamos tu sitio web específico con el
              ICD™ y entregamos hallazgos que aplican a tu empresa, no a un
              sector en abstracto.
            </p>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          ease: 'easeOut',
          delay: 0.2
        }} data-id="element-172" data-mp="2g9833jc0ofci">
            {!isSubmitted ? <div className="bg-arena/5 border border-arena/15 rounded-[12px] p-6 md:p-10" data-id="element-173" data-mp="1rujocdypmcph">
                <p className="font-body text-[13px] text-arena/50 mb-6 text-center" data-id="element-174" data-mp="v6wxcvkenmjf">
                  6 campos · Menos de 2 minutos
                </p>
                <form onSubmit={handleSubmit} action="https://formspree.io/f/xjgdkoor" method="POST" className="flex flex-col" data-id="element-175" data-mp="1458p50x0cqdg">
                  <input type="text" name="name" placeholder="Tu nombre completo" required onChange={handleChange} className={inputClass} data-id="element-176" data-mp="bg1re45do4t9" />
                  <input type="text" name="company" placeholder="Nombre de la empresa" required onChange={handleChange} className={inputClass} data-id="element-177" data-mp="3x28s0pqqhay" />
                  <select name="sector" required defaultValue="" onChange={handleChange} className={`${inputClass} appearance-none ${formData.sector ? 'text-arena' : 'text-arena/35'}`} data-id="element-178" data-mp="3m2wzq4ji5cog">
                    <option value="" disabled data-id="element-179" data-mp="37ekdw0e4pa7o">
                      Selecciona tu sector
                    </option>
                    <option value="Firma Legal" className="text-carbon" data-id="element-180" data-mp="33cytzv36fvnz">
                      Firma Legal
                    </option>
                    <option value="Logística y Transporte" className="text-carbon" data-id="element-181" data-mp="2yee2ck1zqkb5">
                      Logística y Transporte
                    </option>
                    <option value="Comercio Exterior" className="text-carbon" data-id="element-182" data-mp="v1muoyrd2r3w">
                      Comercio Exterior
                    </option>
                    <option value="Consultoría" className="text-carbon" data-id="element-183" data-mp="l4s2exlolmy2">
                      Consultoría
                    </option>
                    <option value="Servicios Profesionales" className="text-carbon" data-id="element-184" data-mp="7cbubidxayqa">
                      Servicios Profesionales
                    </option>
                    <option value="Otro sector B2B" className="text-carbon" data-id="element-185" data-mp="2f2789e0az5m">
                      Otro sector B2B
                    </option>
                  </select>
                  <input type="text" name="website" placeholder="www.tuempresa.com (si ya tienes sitio web)" onChange={handleChange} className={inputClass} data-id="element-186" data-mp="3kn1exvqrmo0t" />
                  <input type="email" name="email" placeholder="correo@tuempresa.com" required onChange={handleChange} className={inputClass} data-id="element-187" data-mp="310g93o0cg7bp" />

                  <select name="challenge" required defaultValue="" onChange={handleChange} className={`${inputClass} appearance-none ${formData.challenge ? 'text-arena' : 'text-arena/35'}`} data-id="element-188" data-mp="3ow28usqdrywp">
                    <option value="" disabled data-id="element-189" data-mp="3tp52f0v5r8cw">
                      ¿Cuál es tu mayor desafío digital?
                    </option>
                    <option value="Generar más consultas de potenciales clientes" className="text-carbon" data-id="element-190" data-mp="bg4hp4htomxc">
                      Generar más consultas de potenciales clientes
                    </option>
                    <option value="Mejorar la imagen y credibilidad de la empresa" className="text-carbon" data-id="element-191" data-mp="ejc4h2ay1d0o">
                      Mejorar la imagen y credibilidad de la empresa
                    </option>
                    <option value="Diferenciarnos de la competencia en digital" className="text-carbon" data-id="element-192" data-mp="129yako1e0n81">
                      Diferenciarnos de la competencia en digital
                    </option>
                    <option value="Convertir más visitas en clientes reales" className="text-carbon" data-id="element-193" data-mp="sdbro4smg68n">
                      Convertir más visitas en clientes reales
                    </option>
                    <option value="No sé exactamente, por eso quiero el diagnóstico" className="text-carbon" data-id="element-194" data-mp="381vbueg3tup6">
                      No sé exactamente, por eso quiero el diagnóstico
                    </option>
                  </select>

                  <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center bg-arena text-verde font-display font-bold text-[15px] px-8 py-4 rounded-[6px] hover:bg-[#EAE7E0] transition-colors duration-200 mt-2 mb-4 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-195" data-mp="1xh795crk449k">
                    {isSubmitting ? 'Enviando...' : <>
                        Iniciar diagnóstico de mi sitio web{' '}
                        <ArrowRight size={16} className="ml-2" data-id="element-196" data-mp="lw1r2ld3qosw" />
                      </>}
                  </button>

                  {error && <p role="alert" className="font-body text-[13px] text-[#E85555] text-center mb-3" data-id="element-197" data-mp="itniaqyq8fhc">
                      {error}
                    </p>}

                  <p className="font-body text-[12px] text-arena/40 text-center leading-relaxed mt-4" data-id="element-198" data-mp="ssmlhu0q3iwy">
                    Sin compromiso · Sin reunión previa · 48 horas
                    <br data-id="element-199" data-mp="11rzuklkxq5h5" />
                    El análisis lo realiza personalmente el equipo de Origen
                    Studio. No un algoritmo automático.
                  </p>
                </form>
              </div> : <div className="border border-arena/15 rounded-[12px] p-12 text-center" style={{
            backgroundColor: 'rgba(247,244,237,0.08)'
          }} data-id="element-200" data-mp="1bht8vucgoyp3">
                <h3 className="font-display text-[28px] font-bold text-arena mb-4" data-id="element-201" data-mp="1lee9m3hg7nn4">
                  Tu solicitud fue recibida.
                </h3>
                <p className="font-body text-[18px] text-arena/70 leading-[1.6]" data-id="element-202" data-mp="20490jr7sxese">
                  Analizaremos tu sitio web en las próximas 48 horas hábiles y
                  enviaremos el diagnóstico a tu correo.
                </p>
              </div>}
          </motion.div>
        </div>
      </div>
    </section>;
}